import React from 'react';

export default function EditBar({ teacherSlug, isPublic, translations = {} }) {
  const handleEditClick = () => {
    window.location.href = '/dashboard/page/edit';
  };

  if (isPublic) {
    // Public page mode: Blue gradient, simple status + button
    return (
      <div className="edit-bar public">
        <div className="edit-bar-container">
          <div className="edit-bar-content">
            {/* Left side: Status indicator */}
            <div className="edit-bar-status">
              <span className="status-icon">🌐</span>
              <span className="status-text">
                {translations?.edit_bar?.public_page || 'Public Page'}
              </span>
            </div>

            {/* Right side: Edit button */}
            <button onClick={handleEditClick} className="edit-button public">
              <svg className="edit-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              <span>{translations?.edit_bar?.edit_page || 'Edit Page'}</span>
            </button>
          </div>
        </div>

        <style jsx>{`
          .edit-bar.public {
            width: 100%;
            background: linear-gradient(135deg, #3366FF 0%, #2952CC 100%);
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
          }

          .edit-bar-container {
            max-width: 1200px;
            margin: 0 auto;
            padding: 0 1rem;
          }

          .edit-bar-content {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 0.75rem 0;
          }

          .edit-bar-status {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            color: white;
          }

          .status-icon {
            font-size: 1.25rem;
            display: flex;
            align-items: center;
          }

          .status-text {
            font-size: 0.875rem;
            font-weight: 500;
          }

          .edit-button.public {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            padding: 0.5rem 1.25rem;
            background: white;
            color: #3366FF;
            border: none;
            border-radius: 0.5rem;
            font-size: 0.875rem;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          }

          .edit-button.public:hover {
            background: #f8f9fa;
            transform: translateY(-1px);
            box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
          }

          .edit-button.public:active {
            transform: translateY(0);
          }

          .edit-icon {
            width: 1.125rem;
            height: 1.125rem;
          }

          /* Mobile responsive */
          @media (max-width: 640px) {
            .edit-bar-content {
              padding: 0.625rem 0;
            }

            .status-text {
              font-size: 0.8125rem;
            }

            .edit-button.public {
              padding: 0.4375rem 1rem;
              font-size: 0.8125rem;
            }

            .edit-icon {
              width: 1rem;
              height: 1rem;
            }
          }
        `}</style>
      </div>
    );
  }

  // Private page mode: Yellow warning banner with full message
  return (
    <div className="edit-bar private">
      <div className="edit-bar-container-wide">
        <div className="warning-content">
          <svg className="warning-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <div className="warning-text">
            <h3 className="warning-title">
              {translations?.teachers?.private_page_warning_title || '⚠️ This is a private page'}
            </h3>
            <p className="warning-message">
              {translations?.teachers?.private_page_warning_message || 'Only you can see this page. To let students find you, go to the edit page and enable your public page.'}
            </p>
          </div>
          <button onClick={handleEditClick} className="edit-button private">
            <svg className="edit-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            <span>{translations?.teachers?.go_to_edit_page || 'Go to Edit Page'}</span>
          </button>
        </div>
      </div>

      <style jsx>{`
        .edit-bar.private {
          width: 100%;
          background-color: #fefce8;
          border-bottom: 2px solid #facc15;
        }

        .edit-bar-container-wide {
          max-width: 64rem;
          margin: 0 auto;
          padding: 1rem 1.5rem;
        }

        .warning-content {
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
        }

        .warning-icon {
          width: 1.5rem;
          height: 1.5rem;
          color: #ca8a04;
          flex-shrink: 0;
          margin-top: 0.125rem;
        }

        .warning-text {
          flex: 1;
        }

        .warning-title {
          font-size: 0.875rem;
          font-weight: 600;
          color: #713f12;
          margin-bottom: 0.25rem;
        }

        .warning-message {
          font-size: 0.875rem;
          color: #854d0e;
        }

        .edit-button.private {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1.25rem;
          background-color: #ca8a04;
          color: white;
          border: none;
          border-radius: 0.5rem;
          font-size: 0.875rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          flex-shrink: 0;
        }

        .edit-button.private:hover {
          background-color: #a16207;
        }

        .edit-icon {
          width: 1.125rem;
          height: 1.125rem;
        }

        /* Mobile responsive */
        @media (max-width: 640px) {
          .warning-content {
            flex-direction: column;
            gap: 0.75rem;
          }

          .edit-button.private {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
}
